/**
 * How a purchase's fields are drawn for a country and how the buyer's
 * answers are read (LIS-1173, the purchase design's §2.4, §2.5 and §2.7):
 * one set of pure functions the sheet on both clients and the server run, so
 * the form and the start can never disagree. The world's rules (phones,
 * postcodes, regions) come from `./world`; the seller's policy comes on the
 * field.
 */
import { z } from "zod";
import {
  cityLabel,
  countryData,
  matchRegion,
  postcodeNoun,
  readPhone,
  readPostcode,
  regionFits,
  regionLabel,
  regionOptions,
  withPostcodeNoun,
  type RegionOption,
} from "@libs/utils/world";
import {
  addressAnswerSchema,
  phoneAnswerSchema,
  valueAnswerSchema,
  type AddressField,
  type Answers,
  type Destination,
  type Field,
  type Section,
} from "./schemas/purchase";

// Drawing an address for a country (§2.4) ------------------------------------

/** A part the form shows: whether the buyer must fill it, and its label in
 *  the country's words. */
export type ShownPart = { required: boolean; label: string };
export type ShownRegion = ShownPart & {
  /** The country's regions to choose from; null where its regions are
   *  typed. */
  options: RegionOption[] | null;
};
/** An address as the form draws it in one country: each part shown, or
 *  null where the seller's form or the country's addresses have none. */
export type AddressForm = {
  line1: ShownPart | null;
  line2: ShownPart | null;
  city: ShownPart | null;
  region: ShownRegion | null;
  postcode: ShownPart | null;
};
export const addressParts = ["line1", "line2", "city", "region", "postcode"] as const;
export type AddressPart = (typeof addressParts)[number];

/**
 * The address field drawn for `country`: the seller's policy first, the
 * country's data for what the seller leaves to it, as `buyerFieldsFor` has
 * it. A postcode is asked wherever the seller asks one, named as the country
 * names it (ZIP code, Eircode); a region only where the country's addresses
 * require one or list them, required where the seller or the country
 * requires it; a town named as the country names it (an Australian suburb);
 * a second line never required.
 */
export function formFor(field: AddressField, country: string): AddressForm {
  const data = countryData(country);
  const { parts } = field;
  const shown = (policy: "required" | "optional" | "hidden", label: string): ShownPart | null =>
    policy === "hidden" ? null : { required: policy === "required", label };
  const region = data?.region;
  return {
    line1: shown(parts.line1.policy, parts.line1.label),
    line2: shown(parts.line2.policy, parts.line2.label),
    city: shown(parts.city.policy, (data && cityLabel(country)) || parts.city.label),
    postcode: shown(
      parts.postcode.policy,
      data?.postcode ? withPostcodeNoun(parts.postcode.label, postcodeNoun(country)) : parts.postcode.label,
    ),
    region:
      parts.region.policy === "hidden" || !region || (!region.required && !region.options)
        ? null
        : {
            required: parts.region.policy === "required" || region.required,
            label: regionLabel(region.kind),
            options: region.options ? (regionOptions(country) ?? []) : null,
          },
  };
}

/** Where the address field lists `country`: taken, refused with the
 *  seller's sentence, or not listed at all. */
export function countryOf(
  field: AddressField,
  country: string,
): { kind: "accepted" } | { kind: "refused"; message: string } | { kind: "unlisted" } {
  if (field.countries === "all_accepted") return countryData(country) ? { kind: "accepted" } : { kind: "unlisted" };
  const listed = field.countries.find((entry) => entry.code === country);
  if (!listed) return { kind: "unlisted" };
  return listed.kind === "accepted" ? listed : { kind: "refused", message: listed.message };
}

// Reading answers (§2.5) -----------------------------------------------------

/** The buyer's details a field is bound to: an answer to one is saved to
 *  the buyer's identity (`patch`). Any other field (a code the seller
 *  sent) is never saved there. */
export const identityFieldIds = [
  "title",
  "firstName",
  "lastName",
  "birthYear",
  "dateOfBirth",
  "gender",
  "nationality",
  "identityDocument",
  "phone",
  "address",
] as const;
const identityFields: ReadonlySet<string> = new Set(identityFieldIds);

/** A detail of the buyer's identity, by its saved key. */
export type IdentityKey =
  | Exclude<(typeof identityFieldIds)[number], "address">
  | "address"
  | "address2"
  | "city"
  | "region"
  | "postcode"
  | "country"
  | "phoneCountry";

/** What a part of an address is saved as. */
const PART_KEYS: Readonly<Record<AddressPart, IdentityKey>> = {
  line1: "address",
  line2: "address2",
  city: "city",
  region: "region",
  postcode: "postcode",
};

export type AnswerProblem = {
  fieldId: string;
  /** The part of an address the problem is in; null for the field. */
  part: AddressPart | "country" | null;
  code:
    | "unknown_field"
    | "duplicate_answer"
    | "missing"
    | "kind_mismatch"
    | "not_offered"
    | "invalid"
    | "country_refused"
    | "hidden_part";
};

export type ReadAnswers =
  | {
      ok: true;
      /** The seller's buyer data the answers give, each detail read and
       *  normalised: a phone E.164 with the country it is dialled in, a
       *  postcode in the country's form, a region by its value. */
      execution: Partial<Record<IdentityKey, string>>;
      /** The change to the buyer's saved identity: each detail answered
       *  replaces the saved one; null forgets one the form showed and the
       *  buyer left empty, or one the seller asks that the chosen country's
       *  addresses do not have. A detail the form never asked is absent, so
       *  the saved one stays. */
      patch: Partial<Record<IdentityKey, string | null>>;
      /** Answers bound to no identity detail (a code the seller
       *  sent), by field id: never saved. */
      values: Record<string, string>;
    }
  | { ok: false; problems: AnswerProblem[] };

const blank = (value: string | null | undefined) => !value?.trim();
const emailSchema = z.email();

/** A text answer read as its field says: trimmed, an email in lower case,
 *  a document without its separators in capitals. */
function normalised(normalise: "trim" | "email" | "document", value: string): string {
  const trimmed = value.trim();
  if (normalise === "email") return trimmed.toLowerCase();
  if (normalise === "document") return trimmed.replace(/[\s.-]/g, "").toUpperCase();
  return trimmed;
}

/** Today in UTC, as an ISO date. */
const todayOf = (now: Date) => now.toISOString().slice(0, 10);

/** A date answer within its field's bounds: a calendar day or a year, not
 *  before `earliest` and not after today. */
function readDate(field: Extract<Field, { kind: "date" }>, value: string, now: Date): string | null {
  const today = todayOf(now);
  if (field.precision === "year") {
    if (!/^\d{4}$/.test(value)) return null;
    return value >= field.earliest.slice(0, 4) && value <= today.slice(0, 4) ? value : null;
  }
  if (!/^\d{4}-\d{2}-\d{2}$/.test(value)) return null;
  const date = new Date(`${value}T00:00:00Z`);
  if (!Number.isFinite(date.getTime()) || date.toISOString().slice(0, 10) !== value) return null;
  return value >= field.earliest && value <= today ? value : null;
}

/** Every field of the purchase's sections, in order. */
export const fieldsOf = (sections: readonly Section[]): Field[] => sections.flatMap((section) => section.fields);

/**
 * The buyer's answers read against the fields that asked them, the same
 * function on the sheet and at the start. Refused: an answer to no field,
 * two to one, a required field unanswered, an answer of another kind, a
 * choice not offered, a value that is no value of its kind (a phone no
 * whole number of its country, a postcode not of its country), a country
 * the seller refuses or does not list, a part the chosen country's form
 * hides given a value, and a saved password reused for another email.
 */
export function readAnswers(
  fields: readonly Field[],
  answers: Answers,
  options: { now?: Date } = {},
): ReadAnswers {
  const now = options.now ?? new Date();
  const problems: AnswerProblem[] = [];
  const problem = (fieldId: string, code: AnswerProblem["code"], part: AnswerProblem["part"] = null) =>
    problems.push({ fieldId, part, code });
  const byId = new Map<string, Answers[number]["answer"]>();
  const known = new Set(fields.map((field) => field.id));
  for (const { id, answer } of answers) {
    if (!known.has(id)) problem(id, "unknown_field");
    else if (byId.has(id)) problem(id, "duplicate_answer");
    else byId.set(id, answer);
  }
  const execution: Partial<Record<IdentityKey, string>> = {};
  const patch: Partial<Record<IdentityKey, string | null>> = {};
  const values: Record<string, string> = {};
  // A value read: saved to the identity where the field is bound to it,
  // else kept apart.
  const give = (field: Field, key: IdentityKey, value: string) => {
    if (identityFields.has(field.id)) {
      execution[key] = value;
      patch[key] = value;
    } else values[field.id] = value;
  };
  // A shown field left empty: forgotten, where the buyer may leave it so.
  const empty = (field: Field, key: IdentityKey) => {
    if (field.required) problem(field.id, "missing");
    else if (identityFields.has(field.id)) patch[key] = null;
  };
  for (const field of fields) {
    const raw = byId.get(field.id);
    switch (field.kind) {
      case "text": {
        const parsed = raw === undefined ? { value: null } : valueAnswerSchema.safeParse(raw).data;
        if (!parsed) {
          problem(field.id, "kind_mismatch");
          break;
        }
        if (blank(parsed.value)) {
          empty(field, field.id as IdentityKey);
          break;
        }
        const value = normalised(field.normalise, parsed.value!);
        const fits =
          value.length <= field.maxLength &&
          (!field.pattern || new RegExp(`^(?:${field.pattern})$`).test(value)) &&
          (field.normalise !== "email" || emailSchema.safeParse(value).success);
        if (!fits) problem(field.id, "invalid");
        else give(field, field.id as IdentityKey, value);
        break;
      }
      case "choice": {
        const parsed = raw === undefined ? { value: null } : valueAnswerSchema.safeParse(raw).data;
        if (!parsed) problem(field.id, "kind_mismatch");
        else if (blank(parsed.value)) empty(field, field.id as IdentityKey);
        else if (!field.options.some((option) => option.value === parsed.value)) problem(field.id, "not_offered");
        else give(field, field.id as IdentityKey, parsed.value!);
        break;
      }
      case "date": {
        const parsed = raw === undefined ? { value: null } : valueAnswerSchema.safeParse(raw).data;
        if (!parsed) problem(field.id, "kind_mismatch");
        else if (blank(parsed.value)) empty(field, field.id as IdentityKey);
        else {
          const date = readDate(field, parsed.value!.trim(), now);
          if (date === null) problem(field.id, "invalid");
          else give(field, field.id as IdentityKey, date);
        }
        break;
      }
      case "phone": {
        const parsed = raw === undefined ? { success: true as const, data: null } : phoneAnswerSchema.safeParse(raw);
        if (!parsed.success) {
          problem(field.id, "kind_mismatch");
          break;
        }
        if (!parsed.data || blank(parsed.data.number)) {
          if (field.required) problem(field.id, "missing");
          else if (identityFields.has(field.id)) patch.phone = null;
          break;
        }
        const dialled = readPhone(parsed.data.number, parsed.data.country);
        if (!dialled) {
          problem(field.id, "invalid");
          break;
        }
        give(field, "phone", dialled.e164);
        if (identityFields.has(field.id)) {
          execution.phoneCountry = dialled.country;
          patch.phoneCountry = dialled.country;
        }
        break;
      }
      case "address": {
        if (raw === undefined) {
          problem(field.id, "missing");
          break;
        }
        const parsed = addressAnswerSchema.safeParse(raw);
        if (!parsed.success) {
          problem(field.id, "kind_mismatch");
          break;
        }
        readAddress(field, parsed.data, { problem, give, patch });
        break;
      }
    }
  }
  if (problems.length) return { ok: false, problems };
  return { ok: true, execution, patch, values };
}

/** An address answer read for its country (`readAnswers`). */
function readAddress(
  field: AddressField,
  answer: z.infer<typeof addressAnswerSchema>,
  out: {
    problem: (fieldId: string, code: AnswerProblem["code"], part: AnswerProblem["part"]) => void;
    give: (field: Field, key: IdentityKey, value: string) => void;
    patch: Partial<Record<IdentityKey, string | null>>;
  },
) {
  const listed = countryOf(field, answer.country);
  if (listed.kind !== "accepted") {
    out.problem(field.id, listed.kind === "refused" ? "country_refused" : "not_offered", "country");
    return;
  }
  const form = formFor(field, answer.country);
  const bound = identityFields.has(field.id);
  for (const part of addressParts) {
    const shown = form[part];
    const typed = answer[part];
    if (!shown) {
      // A part this country's form hides takes no value. One the seller
      // asks is forgotten (a region left from another country); one it
      // never asks stays saved.
      if (!blank(typed)) out.problem(field.id, "hidden_part", part);
      else if (bound && field.parts[part].policy !== "hidden") out.patch[PART_KEYS[part]] = null;
      continue;
    }
    if (blank(typed)) {
      if (shown.required) out.problem(field.id, "missing", part);
      else if (bound) out.patch[PART_KEYS[part]] = null;
      continue;
    }
    const value = readPart(part, typed!, answer.country, form);
    if (value === null) out.problem(field.id, "invalid", part);
    else out.give(field, PART_KEYS[part], value);
  }
  out.give(field, "country", answer.country);
}

/** One part of an address as the country reads it: a postcode in its
 *  country's form, a region by its value where the country lists them,
 *  else as typed. Null where it is none of the country's. */
function readPart(part: AddressPart, typed: string, country: string, form: AddressForm): string | null {
  if (part === "postcode") {
    const postcode = readPostcode(typed, country);
    return postcode !== null && postcode.length <= 240 ? postcode : null;
  }
  if (part === "region" && form.region?.options) return matchRegion(form.region.options, typed);
  const value = typed.trim();
  if (part === "region" && !regionFits(country, value)) return null;
  return value.length <= 240 ? value : null;
}

// Delivery destinations (§2.7) ---------------------------------------------

/**
 * A delivery destination read against the address field of its purchase:
 * its country taken, its postcode read under the country's rules, its
 * region one of the country's. Nothing else of the address, so a
 * destination never fails for a street or a town. `buyer` once it is the
 * buyer's own (a postcode where the seller asks one and the country has
 * postcodes, a region where the form requires one); else `preliminary`,
 * asked about the country's stand-in, which a start never takes.
 */
export function readDestination(
  destination: Destination,
  field: AddressField,
):
  | { kind: "buyer" | "preliminary"; destination: Destination }
  | { kind: "refused"; problem: AnswerProblem } {
  const refuse = (code: AnswerProblem["code"], part: AnswerProblem["part"]) =>
    ({ kind: "refused", problem: { fieldId: field.id, part, code } }) as const;
  const listed = countryOf(field, destination.country);
  if (listed.kind !== "accepted") return refuse(listed.kind === "refused" ? "country_refused" : "not_offered", "country");
  const form = formFor(field, destination.country);
  let postcode: string | null = null;
  if (!blank(destination.postcode)) {
    if (!form.postcode) return refuse("hidden_part", "postcode");
    postcode = readPart("postcode", destination.postcode!, destination.country, form);
    if (postcode === null) return refuse("invalid", "postcode");
  }
  let region: string | null = null;
  if (!blank(destination.region)) {
    if (!form.region) return refuse("hidden_part", "region");
    region = readPart("region", destination.region!, destination.country, form);
    if (region === null || !regionFits(destination.country, region)) return refuse("invalid", "region");
  }
  const read = { country: destination.country, postcode, region };
  // A country whose addresses have no postcode needs none to be the
  // buyer's (the UAE); one the form makes optional either.
  const postcodeNeeded = Boolean(form.postcode?.required && countryData(destination.country)?.postcode);
  const regionNeeded = Boolean(form.region?.required);
  const complete = (!postcodeNeeded || postcode !== null) && (!regionNeeded || region !== null);
  return { kind: complete ? "buyer" : "preliminary", destination: read };
}

/** Two destinations as one place: the same country, postcode and region,
 *  case and spaces in the postcode aside. */
export function sameDestination(a: Destination, b: Destination): boolean {
  const postcode = (value: string | null) => value?.replace(/\s+/g, "").toUpperCase() ?? null;
  return a.country === b.country && postcode(a.postcode) === postcode(b.postcode) && (a.region ?? null) === (b.region ?? null);
}
